import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { Plus, Wallet, Edit2, Trash2, Calendar, DollarSign, TrendingDown, Filter, ChevronDown, ChevronRight } from "lucide-react";
import { format, parseISO } from "date-fns";

const CURRENCIES = [
  { code: "USD", label: "US Dollar ($)" },
  { code: "EUR", label: "Euro (€)" },
  { code: "GBP", label: "British Pound (£)" },
  { code: "AUD", label: "Australian Dollar (A$)" },
  { code: "CAD", label: "Canadian Dollar (C$)" },
  { code: "INR", label: "Indian Rupee (₹)" },
  { code: "SGD", label: "Singapore Dollar (S$)" },
  { code: "NZD", label: "New Zealand Dollar (NZ$)" },
  { code: "JPY", label: "Japanese Yen (¥)" },
  { code: "CHF", label: "Swiss Franc (CHF)" },
  { code: "ZAR", label: "South African Rand (R)" },
  { code: "AED", label: "UAE Dirham (د.إ)" },
];

const PERIODS = ["monthly", "quarterly", "yearly", "one-time"];

const currency = (n, code) => {
  if (!code) return String(n);
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 2 }).format(Number(n) || 0); }
  catch { return `${code} ${Number(n).toFixed(2)}`; }
};

export default function BudgetsPage() {
  const confirm = useConfirmation();
  const [budgets, setBudgets] = useState([]);
  const [children, setChildren] = useState([]);
  const [providers, setProviders] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [childFilter, setChildFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [expensesByBudget, setExpensesByBudget] = useState({});
  const [expenseForm, setExpenseForm] = useState({ amount: "", description: "", date: "" });
  const [form, setForm] = useState({
    name: "",
    amount: "",
    period: "monthly",
    currency: "",
    child_id: "",
    linked_type: "general",
    linked_id: "",
    notes: ""
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [bRes, cRes, pRes, gRes] = await Promise.all([
        apiClient.get("/budgets"),
        apiClient.get("/children"),
        apiClient.get("/providers"),
        apiClient.get("/goals")
      ]);
      setBudgets(bRes.data);
      setChildren(cRes.data);
      setProviders(pRes.data);
      setGoals(gRes.data);
    } catch {
      toast.error("Failed to load budgets");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.currency) {
      toast.error("Please choose a currency");
      return;
    }
    const payload = {
      ...form,
      amount: Number(form.amount),
      child_id: form.child_id || null,
      linked_id: form.linked_type === "general" ? null : (form.linked_id || null)
    };
    try {
      if (editingBudget) {
        await apiClient.put(`/budgets/${editingBudget.id}`, payload);
        toast.success("Budget updated");
      } else {
        await apiClient.post("/budgets", payload);
        toast.success("Budget created");
      }
      fetchAll();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save budget");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete budget?", description: "This budget and all of its expenses will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/budgets/${id}`);
      toast.success("Budget deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const openEdit = (b) => {
    setEditingBudget(b);
    setForm({
      name: b.name,
      amount: String(b.amount),
      period: b.period,
      currency: b.currency || "",
      child_id: b.child_id || "",
      linked_type: b.linked_type || "general",
      linked_id: b.linked_id || "",
      notes: b.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({ name: "", amount: "", period: "monthly", currency: "", child_id: "", linked_type: "general", linked_id: "", notes: "" });
    setEditingBudget(null);
    setDialogOpen(false);
  };

  const toggleExpand = async (b) => {
    if (expandedId === b.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(b.id);
    if (!expensesByBudget[b.id]) {
      try {
        const r = await apiClient.get(`/budgets/${b.id}/expenses`);
        setExpensesByBudget(prev => ({ ...prev, [b.id]: r.data }));
      } catch { /* ignore */ }
    }
  };

  const handleAddExpense = async (e, budgetId) => {
    e.preventDefault();
    if (!expenseForm.amount) return;
    const amountNum = Number(expenseForm.amount);
    try {
      await apiClient.post(`/budgets/${budgetId}/expenses`, {
        amount: amountNum,
        description: expenseForm.description,
        date: expenseForm.date || undefined
      });
      toast.success("Expense added");
      // Optimistic update so the progress bar reflects the new spend immediately
      setBudgets(prev => prev.map(b => b.id === budgetId
        ? { ...b, spent: Number(b.spent || 0) + amountNum, expense_count: (b.expense_count || 0) + 1 }
        : b
      ));
      const r = await apiClient.get(`/budgets/${budgetId}/expenses`);
      setExpensesByBudget(prev => ({ ...prev, [budgetId]: r.data }));
      await fetchAll();
      setExpenseForm({ amount: "", description: "", date: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add expense");
    }
  };

  const handleDeleteExpense = async (budgetId, expenseId) => {
    if (!(await confirm({ title: "Delete expense?", description: "This expense will be permanently removed from the budget." }))) return;
    const existing = (expensesByBudget[budgetId] || []).find(e => e.id === expenseId);
    const refund = Number(existing?.amount || 0);
    try {
      await apiClient.delete(`/budgets/${budgetId}/expenses/${expenseId}`);
      // Optimistic
      setBudgets(prev => prev.map(b => b.id === budgetId
        ? { ...b, spent: Math.max(0, Number(b.spent || 0) - refund), expense_count: Math.max(0, (b.expense_count || 0) - 1) }
        : b
      ));
      const r = await apiClient.get(`/budgets/${budgetId}/expenses`);
      setExpensesByBudget(prev => ({ ...prev, [budgetId]: r.data }));
      await fetchAll();
    } catch { toast.error("Failed to delete expense"); }
  };

  const childName = (id) => children.find(c => c.id === id)?.name || "All children";
  const linkedLabel = (b) => {
    if (b.linked_type === "provider") return providers.find(p => p.id === b.linked_id)?.name || "Therapist";
    if (b.linked_type === "goal") return goals.find(g => g.id === b.linked_id)?.title || "Goal";
    return "General";
  };

  const filtered = childFilter === "all"
    ? budgets
    : childFilter === "combined"
      ? budgets.filter(b => !b.child_id)
      : budgets.filter(b => b.child_id === childFilter);

  const totalAllocated = filtered.reduce((s, b) => s + Number(b.amount || 0), 0);
  const totalSpent = filtered.reduce((s, b) => s + Number(b.spent || 0), 0);
  const currencies = [...new Set(filtered.map(b => b.currency).filter(Boolean))];
  const summaryCurrency = currencies.length === 1 ? currencies[0] : null;
  const fmtSummary = (n) => summaryCurrency ? currency(n, summaryCurrency) : `${currencies.length} currencies`;

  if (loading) return <div className="space-y-6 animate-pulse"><div className="h-10 bg-muted rounded-lg w-48" /><div className="h-40 bg-muted rounded-3xl" /></div>;

  return (
    <div className="space-y-6" data-testid="budgets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <Wallet className="h-8 w-8" />
            Budgets
          </h1>
          <p className="text-muted-foreground mt-1">Track therapy spending against allocations</p>
        </div>

        <div className="flex items-center gap-2">
          <Select value={childFilter} onValueChange={setChildFilter}>
            <SelectTrigger className="w-44 rounded-full" data-testid="budget-child-filter">
              <Filter className="h-4 w-4 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All budgets</SelectItem>
              <SelectItem value="combined">Combined / family</SelectItem>
              {children.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-budget-btn">
                <Plus className="mr-2 h-4 w-4" /> New Budget
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg rounded-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editingBudget ? "Edit Budget" : "New Budget"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. OT therapy 2026" required className="rounded-xl" data-testid="budget-name-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="500" required className="rounded-xl" data-testid="budget-amount-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Period</Label>
                    <Select value={form.period} onValueChange={v => setForm({ ...form, period: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Currency <span className="text-destructive">*</span></Label>
                  <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })} required>
                    <SelectTrigger className="rounded-xl" data-testid="budget-currency-select">
                      <SelectValue placeholder="Choose a currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>For which child?</Label>
                  <Select value={form.child_id || "__none__"} onValueChange={v => setForm({ ...form, child_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Combined / family" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Combined / family (no specific child)</SelectItem>
                      {children.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Allocate to</Label>
                  <Select value={form.linked_type} onValueChange={v => setForm({ ...form, linked_type: v, linked_id: "" })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="provider">Specific Therapist</SelectItem>
                      <SelectItem value="goal">Specific Goal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.linked_type === "provider" && (
                  <div className="space-y-2">
                    <Label>Therapist</Label>
                    <Select value={form.linked_id} onValueChange={v => setForm({ ...form, linked_id: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select therapist" /></SelectTrigger>
                      <SelectContent>
                        {providers.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.linked_type === "goal" && (
                  <div className="space-y-2">
                    <Label>Goal</Label>
                    <Select value={form.linked_id} onValueChange={v => setForm({ ...form, linked_id: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select goal" /></SelectTrigger>
                      <SelectContent>
                        {goals.map(g => (<SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl resize-none" rows={2} />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">Cancel</Button>
                  <Button type="submit" className="flex-1 rounded-full" data-testid="save-budget-btn">{editingBudget ? "Update" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary */}
      {filtered.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg rounded-3xl"><CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Total Allocated</p>
            <p className="font-heading text-2xl font-bold text-primary mt-1">{fmtSummary(totalAllocated)}</p>
          </CardContent></Card>
          <Card className="border-0 shadow-lg rounded-3xl"><CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Total Spent</p>
            <p className="font-heading text-2xl font-bold text-coral-500 mt-1">{fmtSummary(totalSpent)}</p>
          </CardContent></Card>
          <Card className="border-0 shadow-lg rounded-3xl"><CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Remaining</p>
            <p className="font-heading text-2xl font-bold text-success mt-1">{fmtSummary(Math.max(0, totalAllocated - totalSpent))}</p>
          </CardContent></Card>
          <Card className="border-0 shadow-lg rounded-3xl"><CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Budgets</p>
            <p className="font-heading text-2xl font-bold mt-1">{filtered.length}</p>
          </CardContent></Card>
        </div>
      )}

      {filtered.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <Wallet className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Budgets Yet</h3>
            <p className="text-muted-foreground mb-6">Track funding allocated to therapists or specific goals</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full" data-testid="empty-add-budget-btn">
              <Plus className="mr-2 h-4 w-4" /> Create Budget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((b) => {
            const spent = Number(b.spent || 0);
            const amount = Number(b.amount || 0);
            const pct = amount > 0 ? Math.min(100, Math.round((spent / amount) * 100)) : 0;
            const over = spent > amount;
            const expanded = expandedId === b.id;
            return (
              <Card key={b.id} className="border-0 shadow-lg rounded-3xl" data-testid={`budget-card-${b.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <button onClick={() => toggleExpand(b)} className="flex-1 text-left" data-testid={`budget-expand-${b.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                        <h3 className="font-heading font-bold text-lg">{b.name}</h3>
                        <Badge variant="secondary" className="rounded-full text-xs">{b.period}</Badge>
                        {b.currency && <Badge variant="outline" className="rounded-full text-xs font-mono">{b.currency}</Badge>}
                        <Badge variant="outline" className="rounded-full text-xs">{linkedLabel(b)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground ml-6">
                        {b.child_id ? childName(b.child_id) : "Combined / family"}
                        {b.notes && <> · {b.notes}</>}
                      </p>
                    </button>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(b)} className="rounded-full h-9 w-9" data-testid={`edit-budget-${b.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)} className="rounded-full h-9 w-9 text-destructive" data-testid={`delete-budget-${b.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4 ml-6">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-sm font-medium">{currency(spent, b.currency)} <span className="text-muted-foreground font-normal">of {currency(amount, b.currency)}</span></span>
                      <span className={`text-xs font-bold ${over ? "text-destructive" : pct >= 80 ? "text-coral-500" : "text-muted-foreground"}`}>{pct}%{over && " · OVER"}</span>
                    </div>
                    <Progress value={pct} className={over ? "bg-destructive/15" : ""} />
                  </div>

                  {expanded && (
                    <div className="mt-5 ml-6 pt-4 border-t border-border space-y-3" data-testid={`expenses-list-${b.id}`}>
                      <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider flex items-center gap-1.5"><TrendingDown className="h-3.5 w-3.5" /> Expense ledger</p>

                      <form onSubmit={(e) => handleAddExpense(e, b.id)} className="flex flex-wrap gap-2 items-end" data-testid={`expense-form-${b.id}`}>
                        <div className="flex-1 min-w-[120px]">
                          <Label className="text-xs">Amount</Label>
                          <Input type="number" step="0.01" min="0" placeholder="50.00" value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="rounded-xl h-9" data-testid={`expense-amount-${b.id}`} />
                        </div>
                        <div className="flex-[2] min-w-[180px]">
                          <Label className="text-xs">Description</Label>
                          <Input placeholder="e.g. OT session" value={expenseForm.description} onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })} className="rounded-xl h-9" />
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <Label className="text-xs">Date</Label>
                          <Input type="date" value={expenseForm.date} onChange={e => setExpenseForm({ ...expenseForm, date: e.target.value })} className="rounded-xl h-9" />
                        </div>
                        <Button type="submit" size="sm" className="rounded-full h-9" data-testid={`add-expense-btn-${b.id}`}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                      </form>

                      {(expensesByBudget[b.id] || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground italic py-2">No expenses logged yet.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(expensesByBudget[b.id] || []).map(exp => (
                            <div key={exp.id} className="flex items-center justify-between py-2 px-3 rounded-xl bg-muted/40 text-sm" data-testid={`expense-row-${exp.id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <DollarSign className="h-3.5 w-3.5 text-coral-500 flex-shrink-0" />
                                <span className="font-mono font-semibold">{currency(exp.amount, b.currency)}</span>
                                <span className="text-muted-foreground truncate">{exp.description || "—"}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                {exp.date && format(parseISO(exp.date), "MMM d, yyyy")}
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteExpense(b.id, exp.id)} className="h-7 w-7 rounded-full text-destructive">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
